import { Mail, Phone } from "lucide-react";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";

export default function ServizioDetailTemplate({
  testId,
  categoriaLabel,
  titolo,
  heroImage,
  heroImageAlt,
  badgeLogo = "/logo-promoter.webp",
  badgeAlt = "Promo.Ter — Formazione e servizi per il terziario",
  leadText,
  introText,
  banner,
  sezioneLabel = "I servizi dedicati ai nostri associati",
  sezioneTitolo = "Cosa offriamo",
  servizi,
  badges,
  faq,
  calloutBadge,
  ctaText,
  email = "commerciale.promoter@confcommercioroma.it",
  telefoni = ["06.68437.606", "06.68437.530"],
}) {
  const slug = testId || titolo.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <main id="main-content" data-testid={`${slug}-page`}>
      {/* Hero */}
      <section className="relative h-[300px] md:h-[360px] bg-[var(--confcom-blue)] overflow-hidden">
        <img
          src={heroImage}
          alt={heroImageAlt}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-2 block" data-testid="hero-categoria-label">
                {categoriaLabel}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">
                {titolo}
              </h1>
            </div>

            <div
              className="shrink-0 inline-flex flex-col items-center gap-1.5 bg-white rounded-2xl px-5 py-3 shadow-lg border border-white/10"
              data-testid="hero-erogatore-badge"
              aria-label={badgeAlt}
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--confcom-text-secondary)] font-['Encode_Sans_Semi_Condensed']">
                Servizio a cura di
              </span>
              <img src={badgeLogo} alt={badgeAlt} className="h-16 sm:h-20 w-auto object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-[var(--confcom-text-primary)] font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed mb-5">
                {leadText}
              </p>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed">{introText}</p>

              {badges && badges.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {badges.map((b) => (
                    <span
                      key={b}
                      className="text-xs font-semibold text-[var(--confcom-blue)] bg-[var(--confcom-bg-blue-light)] px-3 py-1.5 rounded-full"
                      data-testid={`badge-${b.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {b}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="h-[340px] rounded-xl overflow-hidden" data-testid={`${slug}-intro-image`}>
              <img src={heroImage} alt={heroImageAlt} className="w-full h-full object-cover object-center" />
            </div>
          </div>
        </div>
      </section>

      {/* Banner / citazione */}
      {banner && (
        <section className="py-10 bg-[var(--confcom-blue)]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-lg md:text-xl text-white font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed">
              {banner}
            </p>
          </div>
        </section>
      )}

      {/* Cosa offriamo */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-3 block">{sezioneLabel}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              {sezioneTitolo}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {servizi.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.titolo}
                  className="bg-white p-6 rounded-xl border border-[var(--confcom-border)]"
                  data-testid={`servizio-${item.titolo.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="w-11 h-11 bg-[var(--confcom-bg-blue-light)] rounded-xl flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">
                    {item.titolo}
                  </h3>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq && faq.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] tracking-tight mb-10">
              Domande frequenti
            </h2>
            <div className="space-y-6 max-w-4xl">
              {faq.map((f) => (
                <div key={f.domanda} className="border-b border-[var(--confcom-border)] pb-6" data-testid={`faq-${f.domanda.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{f.domanda}</h3>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{f.risposta}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.95)" }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          {calloutBadge && (
            <span className="inline-block bg-[#56B7E6] text-[var(--confcom-blue)] text-xs font-bold uppercase tracking-wide px-4 py-1.5 rounded-full mb-5">
              {calloutBadge}
            </span>
          )}
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] mb-4">Per maggiori informazioni</h2>
          <p className="text-white/80 mb-8">{ctaText}</p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold hover:opacity-90 transition-opacity"
              data-testid={`cta-email-${slug}`}
            >
              <Mail size={16} aria-hidden="true" /> {email}
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-y-2 text-sm text-white/80">
            <Phone size={15} className="text-white/70 mr-3" aria-hidden="true" />
            {telefoni.map((tel, i) => (
              <span key={tel} className="flex items-center">
                <a href={`tel:+39${tel.replace(/\./g, "")}`} className="hover:text-white transition-colors">
                  {tel}
                </a>
                {i < telefoni.length - 1 && (
                  <span className="w-1 h-1 rounded-full bg-white/40 mx-3" aria-hidden="true" />
                )}
              </span>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
